import { FC } from 'react'
import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { IHubOrdersTimelineChartItem } from '../../Services/api/dashboard/hub-dashboard.interface'

export interface IChartDataItem {
  name: string
  value: number
  fill: string
}

interface IDashboardChartPanelProps {
  title: string
  subtitle?: string
  data: IChartDataItem[]
  loading?: boolean
  type: 'bar' | 'pie'
  emptyMessage?: string
}

const CHART_HEIGHT = 240
const BAR_CHART_HEIGHT = 280
const TIMELINE_CHART_HEIGHT = 300

const ChartTooltip: FC<{ active?: boolean; payload?: Array<{ payload: IChartDataItem }> }> = ({
  active,
  payload,
}) => {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" fontWeight={700}>
        {item.name}: {item.value.toLocaleString('pt-BR')}
      </Typography>
    </Box>
  )
}

const TimelineTooltip: FC<{
  active?: boolean
  label?: string
  payload?: Array<{ name: string; value: number; color: string }>
}> = ({ active, label, payload }) => {
  if (!active || !payload?.length) return null

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((item) => (
        <Typography key={item.name} variant="caption" display="block" sx={{ color: item.color }}>
          {item.name}: {Number(item.value || 0).toLocaleString('pt-BR')}
        </Typography>
      ))}
    </Box>
  )
}

export const DashboardChartPanel: FC<IDashboardChartPanelProps> = ({
  title,
  subtitle,
  data,
  loading,
  type,
  emptyMessage = 'Sem dados para exibir.',
}) => {
  const hasData = data.some((item) => item.value > 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: type === 'bar' ? BAR_CHART_HEIGHT : CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <CircularProgress size={28} />
        ) : !hasData ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        ) : type === 'bar' ? (
          <ResponsiveContainer width="100%" height={BAR_CHART_HEIGHT}>
            <BarChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 4 }}>
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-25}
                textAnchor="end"
                height={72}
                tickMargin={8}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={48} tickMargin={4} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <PieChart>
              <Pie
                data={data.filter((d) => d.value > 0)}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={78}
                paddingAngle={2}
              >
                {data
                  .filter((d) => d.value > 0)
                  .map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  )
}

export const DashboardTimelinePanel: FC<{
  title: string
  subtitle?: string
  data: IHubOrdersTimelineChartItem[]
  loading?: boolean
  emptyMessage?: string
}> = ({
  title,
  subtitle,
  data,
  loading,
  emptyMessage = 'Sem dados para exibir.',
}) => {
  const hasData = data.some((item) => item.created > 0 || item.completed > 0 || item.canceled > 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: TIMELINE_CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <CircularProgress size={28} />
        ) : !hasData ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={TIMELINE_CHART_HEIGHT}>
            <LineChart data={data} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} minTickGap={18} tickMargin={8} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={48} tickMargin={4} />
              <Tooltip content={<TimelineTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
              <Line type="monotone" dataKey="created" name="Criados" stroke="#1976d2" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="completed" name="Concluídos" stroke="#2e7d32" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="canceled" name="Cancelados" stroke="#d32f2f" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  )
}
